import React, { useEffect, useState } from "react";
import { api, imgUrl, money } from "../../lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import DataTable, { Pagination } from "../../components/admin/DataTable";
import AdminDialog from "../../components/admin/AdminDialog";
import { useFeatures } from "../../context/FeaturesContext";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2 } from "lucide-react";

const APPROVALS = ["", "pending", "approved", "rejected"];

export default function AdminProducts() {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    q: "",
    approval: searchParams.get("approval") || "",
    status: searchParams.get("status") || "",
  });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/admin/products", { params });
      // Defensive: API contract is {items: [], total} but a stale SW or proxy
      // could return something else. Normalize so downstream .map/.length never throws.
      setData({
        items: Array.isArray(data?.items) ? data.items : [],
        total: typeof data?.total === "number" ? data.total : 0,
      });
      setSelected(new Set());
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.approval, filters.status, page]);

  const setApproval = async (id, approval) => {
    const note = approval === "rejected" ? window.prompt("Reason?") : null;
    if (approval === "rejected" && !note) return;
    try {
      await api.put(`/admin/products/${id}/approval`, { approval, approval_note: note });
      toast.success(`Product ${approval}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleFlag = async (p, flag) => {
    try {
      await api.put(`/admin/products/${p.id}/flags`, { [flag]: !p[flag] });
      toast.success("Updated");
      load();
    } catch { toast.error("Failed"); }
  };

  const openEdit = (p) => {
    setEditing(p);
    setEditForm({
      title: p.title || "", description: p.description || "", price: p.price ?? 0, mrp: p.mrp ?? 0,
      color: p.color || "", fabric: p.fabric || "", occasion: p.occasion || "", status: p.status || "active",
    });
  };

  const saveEdit = async () => {
    if (!editForm.title?.trim()) return toast.error("Title is required");
    const price = Number(editForm.price);
    const mrp = Number(editForm.mrp);
    if (!Number.isFinite(price) || price < 0) return toast.error("Price must be a non-negative number");
    if (!Number.isFinite(mrp) || mrp < 0) return toast.error("MRP must be a non-negative number");
    try {
      await api.put(`/admin/products/${editing.id}`, { ...editForm, title: editForm.title.trim(), price, mrp });
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const [bulkConfirm, setBulkConfirm] = useState(null);   // { action: 'approved'|'rejected' }
  const [rejectNote, setRejectNote] = useState("");
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const [tryOnStyle, setTryOnStyle] = useState("studio");
  const [tryOnSubmitting, setTryOnSubmitting] = useState(false);
  const { has: hasFeature } = useFeatures();
  const navigate = useNavigate();

  const startBulkTryOn = async () => {
    setTryOnSubmitting(true);
    try {
      const { data } = await api.post("/admin/try-on/batches", {
        product_ids: Array.from(selected),
        style: tryOnStyle,
      });
      toast.success(`Batch started for ${data.product_count} product(s)`);
      setTryOnOpen(false);
      navigate(`/admin/try-on/batches/${data.batch_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start batch");
    } finally {
      setTryOnSubmitting(false);
    }
  };

  const toggleRow = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected((s) => {
      if (s.size === data.items.length) return new Set();
      return new Set(data.items.map((p) => p.id));
    });
  };
  const requestBulk = (action) => {
    if (selected.size === 0) return;
    setRejectNote("");
    setBulkConfirm({ action });
  };
  const runBulk = async () => {
    if (!bulkConfirm) return;
    const action = bulkConfirm.action;
    if (action === "rejected" && !rejectNote.trim()) {
      toast.error("Please enter a reason");
      return;
    }
    try {
      const { data } = await api.post("/admin/products/bulk-approve", {
        ids: Array.from(selected),
        action,
        note: action === "rejected" ? rejectNote.trim() : null,
      });
      toast.success(`${data.modified} product(s) ${action}`);
      setBulkConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    }
  };

  const columns = [
    {
      key: "select", label: (
        <input
          type="checkbox"
          checked={data.items.length > 0 && selected.size === data.items.length}
          onChange={toggleAll}
          aria-label="select all"
          data-testid="bulk-select-all"
        />
      ),
      render: (r) => (
        <input
          type="checkbox"
          checked={selected.has(r.id)}
          onChange={() => toggleRow(r.id)}
          aria-label={`select ${r.title}`}
          data-testid={`bulk-select-${r.slug}`}
        />
      ),
    },
    {
      key: "title", label: "Product",
      render: (r) => (
        <div className="flex gap-3">
          {r.images?.[0] ? <img src={imgUrl(r.images[0])} className="w-12 aspect-[3/4] object-cover" alt="" /> : <div className="w-12 aspect-[3/4] bg-[hsl(var(--kb-surface-alt))]" />}
          <div>
            <Link to={`/product/${r.slug}`} className="underline font-medium">{r.title}</Link>
            <div className="text-xs text-[hsl(var(--kb-muted))]">{money(r.price)} · {r.color || "—"} / {r.fabric || "—"}</div>
          </div>
        </div>
      ),
    },
    { key: "approval", label: "Approval", render: (r) => <span className={`uppercase text-xs ${r.approval === "approved" ? "text-green-700" : r.approval === "rejected" ? "text-red-700" : "text-[hsl(var(--kb-primary))]"}`}>{r.approval || "pending"}</span> },
    { key: "status", label: "Status", render: (r) => <span className="uppercase text-xs">{r.status}</span> },
    {
      key: "flags", label: "Flags",
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          <button onClick={() => toggleFlag(r, "is_featured")} className={`chip ${r.is_featured ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : ""}`} data-testid={`flag-featured-${r.slug}`}>Featured</button>
          <button onClick={() => toggleFlag(r, "is_trending")} className={`chip ${r.is_trending ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : ""}`} data-testid={`flag-trending-${r.slug}`}>Trending</button>
          <button onClick={() => toggleFlag(r, "is_ready_to_ship")} className={`chip ${r.is_ready_to_ship ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : ""}`} data-testid={`flag-rts-${r.slug}`}>Ready to Ship</button>
        </div>
      ),
    },
    {
      key: "actions", label: "",
      render: (r) => (
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => openEdit(r)} className="kb-btn-outline !py-1 !px-2 !text-[10px]" data-testid={`edit-${r.slug}`}>Edit</button>
          {r.approval !== "approved" && <button onClick={() => setApproval(r.id, "approved")} className="kb-btn-primary !py-1 !px-2 !text-[10px]" data-testid={`approve-${r.slug}`}>Approve</button>}
          {r.approval !== "rejected" && <button onClick={() => setApproval(r.id, "rejected")} className="kb-btn-outline !py-1 !px-2 !text-[10px]" data-testid={`reject-${r.slug}`}>Reject</button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="admin-products-page" className={selected.size > 0 ? "pb-24" : undefined}>
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Moderation</div>
          <h1 className="font-display text-4xl">Products ({data.total})</h1>
        </div>
        <Link
          to="/admin/products/new"
          className="kb-btn-primary !py-3 !px-5"
          data-testid="admin-product-create-btn"
        >
          + Add Product
        </Link>
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))] p-4 mb-4 grid md:grid-cols-4 gap-3">
        <input className="kb-input md:col-span-2" placeholder="Search title…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())} data-testid="filter-q" />
        <select className="kb-input" value={filters.approval} onChange={(e) => { setFilters({ ...filters, approval: e.target.value }); setPage(1); }} data-testid="filter-approval">
          <option value="">All approvals</option>
          {APPROVALS.filter(Boolean).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="kb-input" value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }} data-testid="filter-status">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="inactive">Inactive</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <DataTable columns={columns} rows={data.items} loading={loading} emptyLabel="No products match" testid="products-table" />

      {selected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-[hsl(var(--kb-primary))] text-white p-4 flex items-center gap-3 flex-wrap shadow-2xl" data-testid="bulk-action-bar">
          <div className="flex-1 min-w-0 text-sm">
            <span className="font-display text-lg">{selected.size}</span> selected
          </div>
          <button onClick={() => setSelected(new Set())} className="text-white/80 underline text-xs uppercase tracking-wider" data-testid="bulk-clear">
            Clear
          </button>
          <button
            onClick={() => requestBulk("approved")}
            className="bg-white text-[hsl(var(--kb-primary))] px-4 py-2 text-xs uppercase tracking-wider font-medium"
            data-testid="bulk-approve"
          >
            Approve {selected.size}
          </button>
          <button
            onClick={() => requestBulk("rejected")}
            className="border border-white/40 px-4 py-2 text-xs uppercase tracking-wider hover:bg-white/10"
            data-testid="bulk-reject"
          >
            Reject {selected.size}
          </button>
          {hasFeature("ai_try_on") && hasFeature("ai_try_on_bulk") && (
            <button
              onClick={() => setTryOnOpen(true)}
              className="border border-white/40 px-4 py-2 text-xs uppercase tracking-wider hover:bg-white/10 inline-flex items-center gap-1"
              data-testid="bulk-try-on"
            >
              <Sparkles size={12} /> AI Try-on {selected.size}
            </button>
          )}
        </div>
      )}

      {bulkConfirm && (
        <AdminDialog
          open={!!bulkConfirm}
          onClose={() => setBulkConfirm(null)}
          title={bulkConfirm.action === "approved" ? `Approve ${selected.size} product(s)` : `Reject ${selected.size} product(s)`}
          subtitle="Bulk action"
          maxWidth="max-w-md"
          testid="bulk-confirm-dialog"
          footer={
            <>
              <button className="kb-btn-outline" onClick={() => setBulkConfirm(null)} data-testid="bulk-cancel">Cancel</button>
              <button className="kb-btn-primary" onClick={runBulk} data-testid="bulk-confirm">
                Confirm {bulkConfirm.action === "approved" ? "Approve" : "Reject"}
              </button>
            </>
          }
        >
          <p className="text-sm">
            You are about to <b>{bulkConfirm.action === "approved" ? "approve" : "reject"}</b> {selected.size} product{selected.size === 1 ? "" : "s"}. This will {bulkConfirm.action === "approved" ? "set their status to active and approval to approved" : "mark them as rejected"}.
          </p>
          {bulkConfirm.action === "rejected" && (
            <div>
              <label className="overline block mb-1">Reason (sent to seller)</label>
              <textarea
                rows={3}
                className="kb-input"
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="e.g. Images do not match the listed product…"
                data-testid="bulk-reject-note"
                autoFocus
              />
            </div>
          )}
        </AdminDialog>
      )}

      <Pagination page={page} limit={20} total={data.total} onPage={setPage} />

      {tryOnOpen && (
        <AdminDialog
          open={tryOnOpen}
          onClose={() => setTryOnOpen(false)}
          title={`AI Try-on for ${selected.size} product(s)`}
          subtitle="Bulk model generation"
          maxWidth="max-w-md"
          testid="bulk-try-on-dialog"
          footer={
            <>
              <button className="kb-btn-outline" onClick={() => setTryOnOpen(false)} data-testid="bulk-try-on-cancel">Cancel</button>
              <button
                className="kb-btn-primary inline-flex items-center gap-2"
                onClick={startBulkTryOn}
                disabled={tryOnSubmitting}
                data-testid="bulk-try-on-start"
              >
                {tryOnSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Start batch
              </button>
            </>
          }
        >
          <p className="text-sm">
            We'll generate 4 angles (front, ¾, side, back) for each of the {selected.size} selected
            product(s). Source = each product's first image. You'll get an in-app notification when done.
          </p>
          <div>
            <div className="overline mb-2">Background style</div>
            <div className="flex gap-2">
              {[{k: "studio", l: "Studio"}, {k: "lifestyle", l: "Lifestyle"}].map((o) => (
                <button
                  key={o.k}
                  type="button"
                  onClick={() => setTryOnStyle(o.k)}
                  className={`px-3 py-2 text-xs border transition ${tryOnStyle === o.k ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : "border-[hsl(var(--kb-border))]"}`}
                  data-testid={`bulk-try-on-style-${o.k}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-[hsl(var(--kb-muted))] bg-[hsl(var(--kb-surface-alt))] p-3">
            Each generation uses your AI add-on quota. The whole batch can take 1-5 minutes depending on size.
          </p>
        </AdminDialog>
      )}

      {editing && (
        <AdminDialog
          open={!!editing}
          onClose={() => setEditing(null)}
          title={editing.title}
          subtitle="Correct Product Data"
          maxWidth="max-w-2xl"
          testid="product-edit-dialog"
          footer={
            <>
              <button className="kb-btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              <button className="kb-btn-primary" onClick={saveEdit} data-testid="prod-edit-save">Save Changes</button>
            </>
          }
        >
          <input className="kb-input" placeholder="Title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="prod-edit-title" />
          <textarea className="kb-input" rows={4} placeholder="Description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
          <div className="grid md:grid-cols-2 gap-3">
            <input type="number" min="0" name="price" className="kb-input" placeholder="Price" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} data-testid="prod-edit-price" />
            <input type="number" min="0" name="mrp" className="kb-input" placeholder="MRP" value={editForm.mrp} onChange={(e) => setEditForm({ ...editForm, mrp: e.target.value })} data-testid="prod-edit-mrp" />
            <input className="kb-input" placeholder="Color" value={editForm.color} onChange={(e) => setEditForm({ ...editForm, color: e.target.value })} />
            <input className="kb-input" placeholder="Fabric" value={editForm.fabric} onChange={(e) => setEditForm({ ...editForm, fabric: e.target.value })} />
            <input className="kb-input" placeholder="Occasion" value={editForm.occasion} onChange={(e) => setEditForm({ ...editForm, occasion: e.target.value })} />
            <select className="kb-input" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </AdminDialog>
      )}
    </div>
  );
}
